import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate } from "../Reducers/actions";
import Modal from "../../../Components/Modal/Modal";

const EmailTemplatesPage = () => {
  const dispatch = useDispatch();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    body: "",
    description: "",
    is_default: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await dispatch(getEmailTemplates());
      setTemplates(response.results || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        subject: template.subject,
        body: template.body,
        description: template.description || "",
        is_default: template.is_default || false
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: "",
        subject: "",
        body: getDefaultBody(),
        description: "",
        is_default: false
      });
    }
    setShowModal(true);
  };

  const getDefaultBody = () => {
    return `Geachte heer/mevrouw,

Hierbij ontvangt u het inspectierapport van {{zwembadnaam}}, uitgevoerd op {{inspectiedatum}}.

Rapportnummer: {{rapportnummer}}
Inspecteur: {{inspecteur}}

In de bijlage vindt u het volledige rapport in PDF-formaat.

Mocht u vragen hebben over dit rapport, dan kunt u contact met ons opnemen.

Met vriendelijke groet,

Zwembadinspecties.nl`;
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData({
      name: "",
      subject: "",
      body: "",
      description: "",
      is_default: false
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      alert("Vul alle verplichte velden in");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await dispatch(updateEmailTemplate(editingTemplate._id, formData));
      } else {
        await dispatch(createEmailTemplate(formData));
      }
      handleCloseModal();
      loadTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      alert("Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Weet je zeker dat je dit sjabloon wilt verwijderen?")) {
      return;
    }

    try {
      await dispatch(deleteEmailTemplate(id));
      loadTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Fout bij verwijderen");
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3>E-mail Sjablonen</h3>
              <button
                className="btn btn-primary"
                onClick={() => handleOpenModal()}
              >
                <i className="fas fa-plus mr-2"></i>
                Nieuw Sjabloon
              </button>
            </div>
            <div className="card-body">
              {/* Info blok */}
              <div className="alert alert-info mb-4">
                <h5><i className="fas fa-info-circle mr-2"></i>Beschikbare variabelen</h5>
                <p className="mb-0">
                  Gebruik deze variabelen in je sjablonen. Ze worden automatisch vervangen bij het versturen:
                </p>
                <code className="d-block mt-2">
                  {"{{zwembadnaam}}"} - Naam van het zwembad<br/>
                  {"{{inspectiedatum}}"} - Datum van de inspectie<br/>
                  {"{{inspecteur}}"} - Naam van de inspecteur<br/>
                  {"{{rapportnummer}}"} - Uniek rapportnummer<br/>
                  {"{{template}}"} - Naam van het inspectie sjabloon<br/>
                  {"{{categorie}}"} - Categorie van de inspectie
                </code>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Laden...</span>
                  </div>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-envelope fa-3x text-muted mb-3"></i>
                  <p className="text-muted">Nog geen e-mail sjablonen aangemaakt</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleOpenModal()}
                  >
                    Maak je eerste sjabloon
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Naam</th>
                        <th>Onderwerp</th>
                        <th>Beschrijving</th>
                        <th style={{width: '100px'}}>Standaard</th>
                        <th style={{width: '150px'}}>Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((template) => (
                        <tr key={template._id}>
                          <td>
                            <strong>{template.name}</strong>
                          </td>
                          <td>{template.subject}</td>
                          <td className="text-muted">{template.description || "-"}</td>
                          <td>
                            {template.is_default ? (
                              <span className="badge badge-success">
                                <i className="fas fa-check mr-1"></i>
                                Standaard
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary mr-2"
                              onClick={() => handleOpenModal(template)}
                              title="Bewerken"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(template._id)}
                              title="Verwijderen"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal voor toevoegen/bewerken */}
      <Modal
        visible={showModal}
        onClose={handleCloseModal}
        width={800}
        className="email-template-modal"
      >
        <div className="p-4">
          <h4 className="mb-4">
            {editingTemplate ? "E-mail Sjabloon Bewerken" : "Nieuw E-mail Sjabloon"}
          </h4>

          <div className="form-group">
            <label><strong>Naam *</strong></label>
            <input
              type="text"
              className="form-control"
              placeholder="Bijv. Rapport verzending"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label><strong>Onderwerp *</strong></label>
            <input
              type="text"
              className="form-control"
              placeholder="Bijv. Inspectierapport {{zwembadnaam}} - {{inspectiedatum}}"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />
            <small className="text-muted">Je kunt variabelen gebruiken zoals {"{{zwembadnaam}}"}</small>
          </div>

          <div className="form-group">
            <label><strong>Bericht *</strong></label>
            <textarea
              className="form-control"
              rows={12}
              placeholder="Schrijf hier je e-mail bericht..."
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label><strong>Beschrijving</strong></label>
            <input
              type="text"
              className="form-control"
              placeholder="Korte beschrijving van dit sjabloon"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id="isDefault"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
              />
              <label className="custom-control-label" htmlFor="isDefault">
                Gebruik als standaard sjabloon
              </label>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              className="btn btn-secondary mr-2"
              onClick={handleCloseModal}
            >
              Annuleren
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2"></span>
                  Opslaan...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Opslaan
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EmailTemplatesPage;
